import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Calendar,
  GraduationCap,
  FileText,
  ArrowRight,
  CheckCircle2,
  Users,
  BookOpen,
  Mail,
} from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { fetchConfig } from "@/lib/api";

const HERO_BG =
  "https://images.pexels.com/photos/31697614/pexels-photo-31697614.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900";

function formatDate(iso) {
  if (!iso) return iso;
  try {
    // Use date-only part to avoid timezone shifts (e.g. 2026-07-15T18:00:00-06:00 → "16 jul" in UTC)
    const datePart = iso.includes("T") ? iso.split("T")[0] : iso;
    const [y, m, day] = datePart.split("-").map(Number);
    const d = new Date(Date.UTC(y, m - 1, day, 12, 0, 0));
    return d.toLocaleDateString("es-MX", {
      day: "2-digit",
      month: "long",
      year: "numeric",
      timeZone: "UTC",
    });
  } catch {
    return iso;
  }
}

export default function Landing() {
  const [config, setConfig] = useState(null);

  useEffect(() => {
    fetchConfig().then(setConfig).catch(console.error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      {/* Hero */}
      <section
        className="relative bg-eje text-white overflow-hidden"
        data-testid="hero-section"
      >
        <div
          className="absolute inset-0 opacity-25"
          style={{
            backgroundImage: `url(${HERO_BG})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
          aria-hidden="true"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-eje/70 via-eje/85 to-eje" />
        <div className="container-wide relative py-20 md:py-32">
          <div className="overline text-gold mb-6 animate-fade-in-up">
            Convocatoria 2026
          </div>
          <h1
            className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] max-w-4xl animate-fade-in-up"
            style={{ animationDelay: "80ms" }}
            data-testid="hero-title"
          >
            Posdoctorado y Estancias de Investigación de la Escuela Judicial
            Electoral
          </h1>
          <p
            className="mt-6 text-base md:text-lg text-white/80 max-w-2xl leading-relaxed animate-fade-in-up"
            style={{ animationDelay: "160ms" }}
          >
            Página de registro para la Segunda Convocatoria del Posdoctorado y
            Estancias de Investigación de la Escuela Judicial Electoral.
          </p>
          <div
            className="mt-10 flex flex-col sm:flex-row gap-3 animate-fade-in-up"
            style={{ animationDelay: "240ms" }}
          >
            <Link
              to="/inscripcion"
              className="inline-flex items-center justify-center gap-2 bg-white text-eje hover:bg-gold hover:text-white px-7 py-3.5 rounded-sm font-medium tracking-wide transition-colors shadow-lg"
              data-testid="start-enrollment-button"
            >
              Iniciar inscripción
              <ArrowRight className="w-4 h-4" />
            </Link>
            <a
              href="https://www.te.gob.mx/eje/media/pdf/5f98124ffd93c96.pdf"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center gap-2 border border-white/30 text-white hover:bg-white/10 px-7 py-3.5 rounded-sm font-medium tracking-wide transition-colors"
              data-testid="learn-more-button"
            >
              Conocer la convocatoria (PDF)
            </a>
          </div>
          <p
            className="text-xs text-white/60 mt-4 animate-fade-in-up"
            style={{ animationDelay: "320ms" }}
          >
            ¿Ya comenzaste tu inscripción? Abre el enlace de reanudación que
            guardaste al iniciar el registro.
          </p>

          {/* Quick stats */}
          {config && (
            <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 max-w-4xl">
              <div className="bg-eje p-5">
                <div className="overline text-gold">Cupo Posdoctorado</div>
                <div className="font-serif text-3xl mt-1">20</div>
              </div>
              <div className="bg-eje p-5">
                <div className="overline text-gold">Cupo Estancias</div>
                <div className="font-serif text-3xl mt-1">3</div>
              </div>
              <div className="bg-eje p-5">
                <div className="overline text-gold">Cierre registro</div>
                <div className="font-serif text-xl mt-1">15 julio 2026</div>
              </div>
              <div className="bg-eje p-5">
                <div className="overline text-gold">Inicio estancias</div>
                <div className="font-serif text-xl mt-1">1 sep 2026</div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Modalidades */}
      <section
        id="convocatoria"
        className="container-wide py-20"
        data-testid="modalidades-section"
      >
        <div className="overline text-eje mb-3">Modalidades disponibles</div>
        <p className="text-slate-600 max-w-2xl mb-12">
          Selecciona la modalidad que mejor se adapte a tu perfil académico y
          al proyecto de investigación que deseas desarrollar.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {(config?.modalidades || []).map((m, idx) => (
            <article
              key={m.key}
              className="card-eje p-8 hover:shadow-md transition-shadow group"
              data-testid={`modalidad-card-${m.key}`}
            >
              <div className="flex items-start justify-between mb-6">
                <div className="w-12 h-12 bg-eje/5 border border-eje/10 flex items-center justify-center text-eje">
                  {idx === 0 ? (
                    <GraduationCap className="w-6 h-6" />
                  ) : idx === 1 ? (
                    <BookOpen className="w-6 h-6" />
                  ) : (
                    <Users className="w-6 h-6" />
                  )}
                </div>
                {m.cupo && (
                  <span className="pill bg-gold/10 text-gold-600 border border-gold/20">
                    {m.cupo} cupos
                  </span>
                )}
              </div>
              <h3 className="font-serif text-2xl text-eje mb-3">{m.label}</h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                {m.descripcion}
              </p>
              <div className="mt-6 pt-6 border-t border-slate-100 flex flex-wrap gap-2">
                {m.modos.map((mode) => (
                  <span
                    key={mode}
                    className="text-xs px-2.5 py-1 bg-slate-50 border border-slate-200 text-slate-600 rounded-sm capitalize"
                  >
                    {mode.replace("_", " ")}
                  </span>
                ))}
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* Calendario */}
      <section className="bg-white border-y border-slate-200">
        <div className="container-wide py-20">
          <div className="overline text-eje mb-3">Calendario oficial</div>
          <h2 className="font-serif text-3xl md:text-4xl text-eje mb-12 max-w-3xl">
            Fechas que debes conocer
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-px bg-slate-200 border border-slate-200">
            <CalendarItem
              icon={<Calendar />}
              label="Apertura del registro"
              date={config && formatDate(config.convocatoria.registro_inicio)}
              detail="10:00 hrs CDMX"
              testid="calendar-inicio"
            />
            <CalendarItem
              icon={<Calendar />}
              label="Cierre del registro"
              date={config && formatDate(config.convocatoria.registro_fin)}
              detail="18:00 hrs CDMX"
              testid="calendar-fin"
            />
            <CalendarItem
              icon={<FileText />}
              label="Resultados preliminares"
              date={config && formatDate(config.convocatoria.resultados_inicio)}
              detail="A partir de 09:00 hrs CDMX"
              testid="calendar-resultados"
            />
            <CalendarItem
              icon={<FileText />}
              label="Resultados definitivos"
              date={config && formatDate(config.convocatoria.notificacion_definitiva)}
              detail="A más tardar"
              testid="calendar-definitivos"
            />
            <CalendarItem
              icon={<CheckCircle2 />}
              label="Publicación de admitidos"
              date={config && formatDate(config.convocatoria.publicacion_admitidos)}
              detail="24 de agosto de 2026"
              testid="calendar-admitidos"
            />
          </div>
        </div>
      </section>

      {/* Líneas de investigación */}
      <section className="container-wide py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          <div>
            <div className="overline text-eje mb-3">Líneas de investigación</div>
            <h2 className="font-serif text-3xl md:text-4xl text-eje mb-6">
              Constitucionalismo, democracia y derecho electoral
            </h2>
            <p className="text-slate-600 leading-relaxed mb-6">
              Los proyectos deberán ser afines al ámbito político-electoral, al
              constitucionalismo, a la democracia o al derecho comparado
              electoral, con incidencia social demostrable.
            </p>
            <Link
              to="/inscripcion"
              className="btn-primary-eje inline-flex items-center gap-2"
              data-testid="cta-inscribirse-bottom"
            >
              Iniciar mi inscripción <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <ul className="space-y-3">
            {(config?.lineas_investigacion || []).map((l) => (
              <li
                key={l.key}
                className="flex items-start gap-3 p-4 bg-white border border-slate-200 rounded-sm"
              >
                <CheckCircle2 className="w-5 h-5 text-gold-600 mt-0.5 flex-shrink-0" />
                <span className="text-slate-800">{l.label}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Recursos oficiales */}
      <section
        className="bg-white border-t border-slate-200"
        data-testid="recursos-section"
      >
        <div className="container-wide py-20">
          <div className="overline text-eje mb-3">Documentación oficial</div>
          <h2 className="font-serif text-3xl md:text-4xl text-eje mb-3 max-w-3xl">
            Descarga los documentos de la convocatoria
          </h2>
          <p className="text-slate-600 mb-10 max-w-2xl">
            Estos son los formatos y avisos oficiales publicados por la Escuela
            Judicial Electoral. Te recomendamos revisarlos antes de iniciar tu
            inscripción.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {(config?.recursos_oficiales || []).map((r) => (
              <a
                key={r.key}
                href={r.url}
                target="_blank"
                rel="noreferrer"
                className="flex items-start gap-3 p-5 border border-slate-200 rounded-sm hover:border-eje hover:bg-eje/[0.03] transition-colors group"
                data-testid={`recurso-${r.key}`}
              >
                <div className="w-10 h-10 bg-eje/5 border border-eje/10 text-eje flex items-center justify-center rounded-sm flex-shrink-0">
                  <FileText className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-eje group-hover:underline">
                    {r.label}
                  </div>
                  <div className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                    Documento oficial · te.gob.mx
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Contacto */}
      <section className="bg-eje/[0.03] border-t border-slate-200">
        <div className="container-wide py-16 text-center">
          <Mail className="w-8 h-8 text-eje mx-auto mb-4" />
          <h2 className="font-serif text-3xl text-eje mb-3">¿Tienes dudas?</h2>
          <p className="text-slate-600 mb-6">
            Para revisión de resultados o información adicional escríbenos a:
          </p>
          <a
            href="mailto:pei.eje@te.gob.mx"
            className="font-serif text-2xl text-eje hover:text-gold-600 transition-colors"
            data-testid="contact-email"
          >
            pei.eje@te.gob.mx
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function CalendarItem({ icon, label, date, detail, testid }) {
  return (
    <div className="bg-white p-6" data-testid={testid}>
      <div className="text-eje mb-3 [&>svg]:w-5 [&>svg]:h-5">{icon}</div>
      <div className="overline text-slate-500 mb-2">{label}</div>
      <div className="font-serif text-xl text-eje leading-tight">
        {date || "—"}
      </div>
      <div className="text-xs text-slate-500 mt-2">{detail}</div>
    </div>
  );
}
